import { useState, useEffect } from 'react';
import { FaCalendar, FaClock, FaWrench, FaCar, FaTimes } from 'react-icons/fa';
import { appointmentService } from '../firebase/appointmentsservice';

const Appointments = () => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [vehicleInfo, setVehicleInfo] = useState({
    make: '',
    model: '',
    year: '',
    issue: ''
  });
  const [loading, setLoading] = useState(false);
  const [userAppointments, setUserAppointments] = useState([]);

  const timeSlots = [
    '09:00', '10:00', '11:00', '13:00',
    '14:00', '15:00', '16:00'
  ];

  const services = [
    'Regular Maintenance',
    'Engine Repair',
    'Brake Service',
    'Transmission Repair',
    'Electrical Systems',
    'Body Work',
    'Tire Service'
  ];

  useEffect(() => {
    fetchUserAppointments();
  }, []);

  const fetchUserAppointments = async () => {
    try {
      const appointments = await appointmentService.getUserAppointments('Pasans19');
      setUserAppointments(appointments);
    } catch (error) {
      alert('Error fetching appointments');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const appointmentData = {
        userId: 'Pasans19', // Using the current user's login
        date: selectedDate,
        time: selectedTime,
        service: selectedService,
        vehicleInfo,
        createdAt: new Date().toISOString() // Store as ISO string instead of Date object
      };

      await appointmentService.createAppointment(appointmentData);
      alert('Appointment booked successfully');
      
      // Reset form
      setSelectedDate('');
      setSelectedTime('');
      setSelectedService('');
      setVehicleInfo({
        make: '',
        model: '',
        year: '',
        issue: ''
      });

      // Refresh appointments list
      fetchUserAppointments();

    } catch (error) {
      alert('Error booking appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (window.confirm('Are you sure you want to cancel this appointment?')) {
      try {
        await appointmentService.deleteAppointment(appointmentId);
        alert('Appointment cancelled successfully');
        fetchUserAppointments();
      } catch (error) {
        alert('Error cancelling appointment');
      }
    }
  };

  // Function to format the date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // If it's already in YYYY-MM-DD format, it's good for display
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // If it's an ISO string or another date format, convert it
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // If invalid date, return original
      return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    } catch (e) {
      return dateString; // Return original if can't parse
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="grid md:grid-cols-2 gap-8">
        {/* Booking Form */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-6">Book an Appointment</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Date Selection */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                <FaCalendar className="inline mr-2" />
                Select Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Time Selection */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                <FaClock className="inline mr-2" />
                Select Time
              </label>
              <select
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Choose a time slot</option>
                {timeSlots.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>

            {/* Service Selection */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                <FaWrench className="inline mr-2" />
                Service Type
              </label>
              <select
                value={selectedService}
                onChange={(e) => setSelectedService(e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a service</option>
                {services.map(service => (
                  <option key={service} value={service}>{service}</option>
                ))}
              </select>
            </div>

            {/* Vehicle Information */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                <FaCar className="inline mr-2" />
                Vehicle Information
              </label>
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Make"
                  value={vehicleInfo.make}
                  onChange={(e) => setVehicleInfo({...vehicleInfo, make: e.target.value})}
                  className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
                <input
                  type="text"
                  placeholder="Model"
                  value={vehicleInfo.model}
                  onChange={(e) => setVehicleInfo({...vehicleInfo, model: e.target.value})}
                  className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <input
                type="number"
                placeholder="Year"
                value={vehicleInfo.year}
                onChange={(e) => setVehicleInfo({...vehicleInfo, year: e.target.value})}
                className="w-full mt-4 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
              <textarea
                placeholder="Describe the issue..."
                value={vehicleInfo.issue}
                onChange={(e) => setVehicleInfo({...vehicleInfo, issue: e.target.value})}
                className="w-full mt-4 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 h-32"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-blue-600 text-white py-3 rounded-lg transition-colors
                ${loading ? 'bg-blue-400 cursor-not-allowed' : 'hover:bg-blue-700'}`}
            >
              {loading ? 'Booking...' : 'Book Appointment'}
            </button>
          </form>
        </div>

        {/* Upcoming Appointments */}
        <div>
          <h2 className="text-2xl font-bold mb-6">Your Appointments</h2>
          <div className="space-y-4">
            {userAppointments.map(appointment => (
              <div key={appointment.id} className="bg-white rounded-lg shadow-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold">{appointment.service}</h3>
                    <p className="text-gray-600">
                      {formatDate(appointment.date)} at {appointment.time}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      {appointment.vehicleInfo.make} {appointment.vehicleInfo.model} ({appointment.vehicleInfo.year})
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteAppointment(appointment.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <FaTimes />
                  </button>
                </div>
                <div className="mt-2">
                  <span className="px-2 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800">
                    Pending
                  </span>
                </div>
              </div>
            ))}
            {userAppointments.length === 0 && (
              <p className="text-gray-500 text-center">No appointments scheduled</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Appointments;